import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Activity, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface CronRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  status: string;
  orgs_processed: number | null;
  orgs_failed: number | null;
  total_proposals_generated: number | null;
  trigger: string | null;
  error: string | null;
}

export function SentinelHealthPanel() {
  const { data: runs, isLoading } = useQuery({
    queryKey: ["sentinel-cron-runs", "recent"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sentinel_cron_runs")
        .select("*")
        .order("started_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data as CronRun[];
    },
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" aria-hidden="true" />
            Saúde do Ciclo Sentinel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  const last = runs?.[0];
  const recent = runs?.slice(0, 5) ?? [];
  // success = 1.0, partial = 0.5, failed/other = 0
  const successRate = recent.length
    ? Math.round(
        (recent.reduce((acc, r) => acc + (r.status === "success" ? 1 : r.status === "partial" ? 0.5 : 0), 0) /
          recent.length) * 100
      )
    : null;

  const lastDurationSec = last?.duration_ms ? Math.round(last.duration_ms / 1000) : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary" aria-hidden="true" />
          Saúde do Ciclo Sentinel
          {last && (
            <Badge
              variant="outline"
              className={
                last.status === "success"
                  ? "border-success/30 text-success"
                  : last.status === "partial"
                  ? "border-warning/30 text-warning"
                  : last.status === "running"
                  ? "border-primary/30 text-primary"
                  : "border-destructive/30 text-destructive"
              }
            >
              {last.status}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!last ? (
          <p className="text-sm text-muted-foreground">
            Nenhum ciclo executado ainda. O cron diário começará a popular esta área.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat
              icon={Clock}
              label="Última execução"
              value={formatDistanceToNow(new Date(last.started_at), { addSuffix: true, locale: ptBR })}
              hint={last.trigger ?? "cron"}
            />
            <Stat
              icon={CheckCircle2}
              label="Taxa de sucesso (5x)"
              value={successRate !== null ? `${successRate}%` : "—"}
              tone={successRate !== null && successRate < 60 ? "warn" : "ok"}
            />
            <Stat
              icon={Activity}
              label="Orgs processadas"
              value={`${last.orgs_processed ?? 0}`}
              hint={last.orgs_failed ? `${last.orgs_failed} com erro` : "todas ok"}
              tone={last.orgs_failed ? "warn" : "ok"}
            />
            <Stat
              icon={AlertTriangle}
              label="Propostas geradas"
              value={`${last.total_proposals_generated ?? 0}`}
              hint={lastDurationSec !== null ? `${lastDurationSec}s de duração` : undefined}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  hint,
  tone = "ok",
}: {
  icon: typeof Activity;
  label: string;
  value: string;
  hint?: string;
  tone?: "ok" | "warn";
}) {
  return (
    <div className="rounded-md border border-border/60 p-2.5">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className={`w-3.5 h-3.5 ${tone === "warn" ? "text-warning" : "text-muted-foreground"}`} aria-hidden="true" />
        {label}
      </div>
      <div className="text-base font-semibold text-foreground mt-0.5">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}
